// Client for the logged-in /v1/codes endpoints.
// Backend: QrCodeController (GET list, GET by id, DELETE archive,
// POST {id}/dynamic convert, POST {id}/protect, DELETE {id}/protect,
// POST {id}/expire, DELETE {id}/expire, GET {id}/image).

import { authedFetch } from "./auth";
import { translateException, translateResponse, type TranslatedError } from "./problem";

export type QrType = "URL" | "TEXT" | "EMAIL" | "SMS" | "GEO" | "WIFI" | "VCARD" | "FILE";
export type QrMode = "STATIC" | "DYNAMIC";
export type QrStatus = "ACTIVE" | "EXPIRED" | "RATE_LIMITED" | "ARCHIVED";
// Mirrors the backend `com.rypox.qrcode.qrcode.domain.QrFrame` enum 1:1.
// Earlier this type listed "ROUND" + "BADGE" because save-mapping
// translated those names on the wire — that mismatch flipped the moment
// the editor stopped reusing the cached server-design on save, so
// frames the user picked (e.g. ROUNDED) hit the backend as "ROUND" and
// were rejected with 400 "Malformed request body".
export type QrFrame = "NONE" | "SIMPLE" | "ROUNDED" | "SHIELD" | "CALL_TO_ACTION";
export type QrEcc = "LOW" | "MEDIUM" | "QUARTILE" | "HIGH";
/** Output formats the editor + renderer support. Upper-case matches the
 *  backend `QrFormat` enum; the front-end's `AnonymousFormat` is lower-
 *  case and is mapped at the save-input layer. */
export type QrFormat = "PNG" | "SVG" | "PDF";
export type WifiAuth = "WPA" | "WEP" | "NOPASS";

export interface QrDesignDto {
  foreground: string;
  background: string;
  logoAssetId: string | null;
  frame: QrFrame;
  ecc?: QrEcc;
  /** Optional QR-spec version floor (1–40). null = encoder auto-pick. */
  minVersion?: number | null;
  /** Editor preference — default download format. null = no preference. */
  format?: QrFormat | null;
  /** Editor preference — default pixel side-length of the raster render. */
  size?: number | null;
}

export interface QrCodeResponse {
  id: string;
  type: QrType;
  mode: QrMode;
  status: QrStatus;
  createdAt: string;
  archivedAt: string | null;
  shortSlug: string | null;
  expiresAt: string | null;
  passwordProtected: boolean;
  /** ADR-025: false = DRAFT (payload + ECC editable), true = LOCKED. */
  locked: boolean;
  lockedAt: string | null;
  payload: Record<string, unknown>;
  design: QrDesignDto;
}

export interface ListCodesFilters {
  includeArchived?: boolean;
  mode?: QrMode;
  type?: QrType;
  limit?: number;
}

export type CodesResult<T> = { ok: true; value: T } | { ok: false; error: TranslatedError };

export async function listCodes(filters: ListCodesFilters = {}): Promise<CodesResult<QrCodeResponse[]>> {
  try {
    const qs = new URLSearchParams();
    if (filters.includeArchived) qs.set("includeArchived", "true");
    if (filters.mode) qs.set("mode", filters.mode);
    if (filters.type) qs.set("type", filters.type);
    if (filters.limit) qs.set("limit", String(filters.limit));
    const path = qs.toString() ? `/v1/codes?${qs}` : "/v1/codes";
    const res = await authedFetch(path, { method: "GET" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse[] };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function getCode(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}`, { method: "GET" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function archiveCode(id: string): Promise<CodesResult<void>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (res.ok) return { ok: true, value: undefined };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function convertToDynamic(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/dynamic`, { method: "POST" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function protectCode(id: string, password: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/protect`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function removeProtection(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/protect`, { method: "DELETE" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function setExpiry(id: string, expiresAt: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/expire`, {
      method: "POST",
      body: JSON.stringify({ expiresAt }),
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function clearExpiry(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/expire`, { method: "DELETE" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

/**
 * GET /v1/codes/{id}/image — returns the rendered image bytes. Format is
 * one of png | svg | pdf. The caller owns the returned Blob (revokeObjectURL
 * after use if turned into an object-URL).
 */
export async function renderCodeImage(
  id: string,
  format: "png" | "svg" | "pdf",
): Promise<CodesResult<Blob>> {
  try {
    const res = await authedFetch(
      `/v1/codes/${encodeURIComponent(id)}/image?format=${format}`,
      { method: "GET" },
    );
    if (res.ok) return { ok: true, value: await res.blob() };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

/**
 * Discriminated union covering all eight QR payload variants the backend
 * PayloadDto accepts. The create-code editor uses a flat form-state; the
 * translator below builds the nested wire-shape
 * `{ url: { url: … } }` / `{ vcard: { fullName: … } }` that the Kotlin
 * `PayloadDto.toDomain()` expects (exactly one property populated).
 */
export type CreateCodePayload =
  | { kind: "url"; url: string }
  | { kind: "text"; text: string }
  | { kind: "email"; to: string; subject?: string; body?: string }
  | { kind: "sms"; to: string; message?: string }
  | { kind: "geo"; latitude: number; longitude: number }
  | { kind: "wifi"; ssid: string; password?: string; authentication: WifiAuth; hidden: boolean }
  | {
      kind: "vcard";
      fullName: string;
      email?: string;
      phone?: string;
      org?: string;
      title?: string;
      url?: string;
    }
  | { kind: "file"; downloadUrl: string };

export interface CreateCodeDesign {
  foreground: string;
  background: string;
  logoAssetId?: string | null;
  frame?: QrFrame;
  ecc?: QrEcc;
  minVersion?: number | null;
  /** Editor preference — default download format. */
  format?: QrFormat | null;
  /** Editor preference — default raster pixel side-length. */
  size?: number | null;
}

export interface CreateCodeOptions {
  /**
   * ADR-019: wire-default is DYNAMIC. The UI toggle on /codes/new lets the
   * user pick STATIC explicitly when they print on durable material
   * (Gravur, Tattoo, Verpackung). When unset the backend default applies.
   */
  mode?: QrMode;
}

function toPayloadBlock(payload: CreateCodePayload): Record<string, unknown> {
  const stripEmpty = <T extends Record<string, unknown>>(o: T): T => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(o)) {
      if (v !== undefined && v !== "" && v !== null) out[k] = v;
    }
    return out as T;
  };
  switch (payload.kind) {
    case "url":
      return { url: { url: payload.url } };
    case "text":
      return { text: { text: payload.text } };
    case "email":
      return {
        email: stripEmpty({ to: payload.to, subject: payload.subject, body: payload.body }),
      };
    case "sms":
      return { sms: stripEmpty({ to: payload.to, message: payload.message }) };
    case "geo":
      return { geo: { latitude: payload.latitude, longitude: payload.longitude } };
    case "wifi":
      return {
        wifi: stripEmpty({
          ssid: payload.ssid,
          password: payload.password,
          authentication: payload.authentication,
          hidden: payload.hidden,
        }),
      };
    case "vcard":
      return {
        vcard: stripEmpty({
          fullName: payload.fullName,
          email: payload.email,
          phone: payload.phone,
          org: payload.org,
          title: payload.title,
          url: payload.url,
        }),
      };
    case "file":
      return { file: { downloadUrl: payload.downloadUrl } };
  }
}

export async function createCode(input: {
  payload: CreateCodePayload;
  design: CreateCodeDesign;
  options?: CreateCodeOptions;
}): Promise<CodesResult<QrCodeResponse>> {
  const designPayload: Record<string, unknown> = {
    foreground: input.design.foreground,
    background: input.design.background,
    logoAssetId: input.design.logoAssetId ?? null,
    frame: input.design.frame ?? "NONE",
  };
  if (input.design.ecc) designPayload.ecc = input.design.ecc;
  if (input.design.minVersion != null) designPayload.minVersion = input.design.minVersion;
  if (input.design.format) designPayload.format = input.design.format;
  if (input.design.size != null) designPayload.size = input.design.size;
  const body: Record<string, unknown> = {
    payload: toPayloadBlock(input.payload),
    design: designPayload,
  };
  if (input.options?.mode) body.mode = input.options.mode;
  try {
    const res = await authedFetch("/v1/codes", { method: "POST", body: JSON.stringify(body) });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

// --- ADR-025 lifecycle endpoints ---------------------------------------

export async function lockCode(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/lock`, { method: "POST" });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function duplicateCode(id: string): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/duplicate`, {
      method: "POST",
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export interface EditDesignInput {
  foreground: string;
  background: string;
  logoAssetId?: string | null;
  frame?: QrFrame;
  ecc?: QrEcc;
  minVersion?: number | null;
  format?: QrFormat | null;
  size?: number | null;
}

function designBody(design: EditDesignInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    foreground: design.foreground,
    background: design.background,
    logoAssetId: design.logoAssetId ?? null,
    frame: design.frame ?? "NONE",
  };
  if (design.ecc) body.ecc = design.ecc;
  if (design.minVersion != null) body.minVersion = design.minVersion;
  if (design.format) body.format = design.format;
  if (design.size != null) body.size = design.size;
  return body;
}

export async function editPayload(
  id: string,
  payload: CreateCodePayload,
  design: EditDesignInput,
): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/payload`, {
      method: "PATCH",
      body: JSON.stringify({ payload: toPayloadBlock(payload), design: designBody(design) }),
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function editRedirectTarget(
  id: string,
  payload: CreateCodePayload,
): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/redirect-target`, {
      method: "PATCH",
      body: JSON.stringify({ payload: toPayloadBlock(payload) }),
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}

export async function editDesign(
  id: string,
  design: EditDesignInput,
): Promise<CodesResult<QrCodeResponse>> {
  try {
    const res = await authedFetch(`/v1/codes/${encodeURIComponent(id)}/design`, {
      method: "PATCH",
      body: JSON.stringify({ design: designBody(design) }),
    });
    if (res.ok) return { ok: true, value: (await res.json()) as QrCodeResponse };
    return { ok: false, error: await translateResponse(res) };
  } catch (ex) {
    return { ok: false, error: translateException(ex) };
  }
}
